import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { ArrowFit16Regular } from "@fluentui/react-icons";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { Component } from "react";
import { createPortal } from "react-dom";

export function getStepSize(event, smallStep, mediumStep, largeStep) {
  if (event.altKey) {
    return smallStep;
  } else if (event.shiftKey) {
    return largeStep;
  }
  return mediumStep;
}

export function clamp(value, min, max) {
  if (value < min) {
    return min;
  } else if (value > max) {
    return max;
  }
  return value;
}

export function toPrecision(value, precision) {
  const p = 1 / precision;
  return Math.round(value * p) / p;
}

const Portal = React.memo(function Portal({ children }) {
  const elRef = useRef(null);

  if (!elRef.current) {
    elRef.current = document.createElement("div");
  }

  useEffect(() => {
    document.body.appendChild(elRef.current);
    return () => {
      try {
        document.body.removeChild(elRef.current);
      } catch (err) {
        console.warn(`Error removing Portal element: ${err}`);
      }
    };
  }, []);

  return createPortal(children, elRef.current);
});

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    pointerEvents: "inherit",
    zIndex: 99999999
  },
  scrubberContainerStyles: {
    cursor: "ew-resize",
    userSelect: "none"
  },
  disabled: {
    cursor: "not-allowed"
  }
});

const Overlay = ({ children }) => {
  const styles = useStyles();
  return <div className={styles.overlay}>{children}</div>;
};

Overlay.propTypes = {
  children: PropTypes.node
};

const Cursor = React.memo(({ x, y }) => {
  const style = {
    transform: `translate(${x}px,${y}px)`,
    position: "absolute",
    width: "20px",
    "& path": {
      stroke: "white",
      strokeWidth: "20px",
      fill: "black"
    }
  };

  return <ArrowFit16Regular style={style} />;
});

Cursor.displayName = "Cursor";

Cursor.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired
};

const Scrubber = ({
  tag,
  children,
  smallStep,
  mediumStep,
  largeStep,
  sensitivity,
  min,
  max,
  precision,
  convertFrom,
  convertTo,
  value,
  onChange,
  onCommit,
  disabled,
  ...rest
}) => {
  // const styles = useLayoutStyles();
  const scrubberEl = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startValue, setStartValue] = useState(null);
  const [delta, setDelta] = useState(null);
  const [mouseX, setMouseX] = useState(null);
  const [mouseY, setMouseY] = useState(null);

  useEffect(() => {
    const handleMouseMove = event => {
      if (isDragging) {
        const newMouseX = mouseX + event.movementX;
        const newMouseY = mouseY + event.movementY;
        const nextDelta = delta + event.movementX;
        const stepSize = getStepSize(event, smallStep, mediumStep, largeStep);
        const nextValue = startValue + Math.round(nextDelta / sensitivity) * stepSize;
        const clampedValue = clamp(nextValue, min, max);
        const roundedValue = precision ? toPrecision(clampedValue, precision) : clampedValue;
        const finalValue = convertTo(roundedValue);
        onChange(finalValue);
        setDelta(nextDelta);
        setMouseX(newMouseX);
        setMouseY(newMouseY);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setStartValue(null);
        setDelta(null);
        setMouseX(null);
        setMouseY(null);

        if (onCommit) {
          onCommit(value);
        } else {
          onChange(value);
        }

        document.exitPointerLock();
      }

      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, mouseX, mouseY, delta, startValue]);

  const handleMouseDown = event => {
    if (disabled) return;
    setStartValue(convertFrom(value));
    setIsDragging(true);
    setDelta(0);
    setMouseX(event.clientX);
    setMouseY(event.clientY);

    scrubberEl.current.requestPointerLock();
  };

  const styles = useStyles();

  return (
    <div
      className={mergeClasses(styles.scrubberContainerStyles, disabled && styles.disabled)}
      as={tag}
      ref={scrubberEl}
      onMouseDown={handleMouseDown}
      {...rest}
    >
      {children}
      {isDragging && !disabled && (
        <Portal>
          <Overlay pointerEvents="none">
            <Cursor x={mouseX} y={mouseY} />
          </Overlay>
        </Portal>
      )}
    </div>
  );
};

Scrubber.propTypes = {
  tag: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  smallStep: PropTypes.number.isRequired,
  mediumStep: PropTypes.number.isRequired,
  largeStep: PropTypes.number.isRequired,
  sensitivity: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  precision: PropTypes.number,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func,
  convertFrom: PropTypes.func.isRequired,
  convertTo: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

Scrubber.defaultProps = {
  tag: "label",
  smallStep: 0.01,
  mediumStep: 0.1,
  largeStep: 0.25,
  sensitivity: 5,
  min: -Infinity,
  max: Infinity,
  value: 0,
  convertFrom: value => value,
  convertTo: value => value
};

export default React.memo(Scrubber);
